import { Environment } from "./Environment";
import { IPermissionResponse } from "../interfaces/IPermissionResponse";
import { IUserPermission } from "../interfaces/IUserPermission";
import { FetchProxy } from "intraactive-sdk-helper";
import { sdkContainerInstance } from "../global/Container";

export interface IGetPermissionsForInstanceResponse {
  hasUniquePermissions: boolean;
  hasSpecialPermissions: boolean;
}

export default class PermissionsService {

  public static getPermissionsForInstance(environment: "Development" | "Test" | "Production", token: string, tenant: string, component: string, instanceName: string): Promise<IGetPermissionsForInstanceResponse> {
    return new Promise((resolve, reject) => {
      if (environment && tenant && token && component && instanceName) {
        const apiUrl = `${Environment.getEnvironmentForPermissionsAPI(environment)}/${tenant.replace(".sharepoint.com", "")}/application/${component}/instance/${encodeURIComponent(instanceName)}/unique`;
        new FetchProxy(apiUrl, {
          method: "GET",
          credentials: "same-origin",
          mode: "cors",
          headers: new Headers({
            "AuthToken": token,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          })
        })
        .withContainer(sdkContainerInstance)
        .fetch()
        .then((response) => {
          if (!response.ok) {
            reject(response);
          } else {
            return response.json();
          }
        }).then(response => {
          resolve(response);
        }).catch(error => {
          console.log(error);
        });
      } else {
        resolve(null);
      }
    });
  }

  public static getPermissionsForApplication(environment: "Development" | "Test" | "Production", token: string, tenant: string, component: string): Promise<any> {
    return new Promise((resolve, reject) => {
      if (environment && tenant && token && component) {
        const apiUrl = `${Environment.getEnvironmentForPermissionsAPI(environment)}/${tenant.replace(".sharepoint.com", "")}/application/${component}/status`;
        new FetchProxy(apiUrl, {
          method: "GET",
          credentials: "same-origin",
          mode: "cors",
          headers: new Headers({
            "AuthToken": token,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          })
        })
          .withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              reject(response);
            } else {
              return response.json();
            }
          }).then(response => {
            resolve(response.isPermissionSet);
          }).catch(error => {
            console.log(error);
          });
      } else {
        resolve(null);
      }
    });
  }

  public static toggleUniquePermissions(environment: "Development" | "Test" | "Production", token: string, tenant: string, component: string, instanceName: string, unique: boolean): Promise<IPermissionResponse> {
    return new Promise((resolve) => {
      if (environment && tenant && token && component) {
        const apiUrl = `${Environment.getEnvironmentForPermissionsAPI(environment)}/${tenant.replace(".sharepoint.com", "")}/application/${component}/instance/${encodeURIComponent(instanceName)}/unique`;
        const body: string = JSON.stringify({ "unique": unique });
        new FetchProxy(apiUrl, {
          method: "POST",
          credentials: "same-origin",
          mode: "cors",
          headers: new Headers({
            "AuthToken": token,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }),
          body: body
        })
          .withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              return response.status;
            } else {
              return response.json();
            }
          }).then(response => {
            resolve(response);
          }).catch(error => {
            console.log(error);
          });
      } else {
        resolve(null);
      }
    });
  }

  public static getPermission(environment: "Development" | "Test" | "Production", token: string, tenant: string, component: string, instanceName?: string): Promise<IUserPermission[]> {
    return new Promise((resolve) => {
      if (environment && tenant && token && component) {
        let apiUrl = `${Environment.getEnvironmentForPermissionsAPI(environment)}/${tenant.replace(".sharepoint.com", "")}/application/${component}`;
        if (instanceName) {
          apiUrl += `/instance/${encodeURIComponent(instanceName)}`;
        }
        new FetchProxy(apiUrl, {
          method: "GET",
          credentials: "same-origin",
          mode: "cors",
          headers: new Headers({
            "AuthToken": token,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          })
        })
          .withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              return response.status;
            } else {
              return response.json();
            }
          }).then(response => {
            resolve(response.userRoles);
          }).catch(error => {
            console.log(error);
          });
      } else {
        resolve(null);
      }
    });
  }

  public static getPermissionsForSite(environment: "Development" | "Test" | "Production", token: string, tenant: string, site: string): Promise<IUserPermission[]> {
    return new Promise((resolve) => {
      if (environment && tenant && token && site) {
        const apiUrl = `${Environment.getEnvironmentForPermissionsAPI(environment)}/${tenant.replace(".sharepoint.com", "")}/site/${site}`;
        new FetchProxy(apiUrl, {
          method: "GET",
          credentials: "same-origin",
          mode: "cors",
          headers: new Headers({
            "AuthToken": token,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          })
        })
          .withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              return response.status;
            } else {
              return response.json();
            }
          }).then(response => {
            resolve(response.userRoles);
          }).catch(error => {
            console.log(error);
          });
      } else {
        resolve(null);
      }
    });
  }

  public static addUserToGroup(environment: "Development" | "Test" | "Production", token: string, tenant: string, component: string, userName: string, userRole: "Administrator" | "Editor" | "Reader", instanceName?: string): Promise<number> {
    return new Promise((resolve) => {
      if (environment && tenant && token && component && userName && userRole) {
        let apiUrl = `${Environment.getEnvironmentForPermissionsAPI(environment)}/${tenant.replace(".sharepoint.com", "")}/application/${component}`;
        if (instanceName) {
          apiUrl += `/instance/${encodeURIComponent(instanceName)}`;
        }
        const body: string = JSON.stringify({
          "loginName": userName,
          "role": userRole
        });
        new FetchProxy(apiUrl, {
          method: "POST",
          credentials: "same-origin",
          mode: "cors",
          headers: new Headers({
            "AuthToken": token,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }),
          body: body
        })
          .withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            resolve(response.status);
          }).catch(error => {
            console.log(error);
            resolve(error);
          });
      } else {
        resolve(null);
      }
    });
  }

  public static deleteUserFromGroup(environment: "Development" | "Test" | "Production", token: string, tenant: string, component: string, userName: string, userRole: "Administrator" | "Editor" | "Reader", instanceName?: string): Promise<number> {
    return new Promise((resolve) => {
      if (environment && tenant && token && component && userName && userRole) {
        let apiUrl = `${Environment.getEnvironmentForPermissionsAPI(environment)}/${tenant.replace(".sharepoint.com", "")}/application/${component}`;
        if (instanceName) {
          apiUrl += `/instance/${encodeURIComponent(instanceName)}`;
        }
        const body: string = JSON.stringify({
          "loginName": userName,
          "role": userRole
        });
        new FetchProxy(apiUrl, {
          method: "DELETE",
          credentials: "same-origin",
          mode: "cors",
          headers: new Headers({
            "AuthToken": token,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }),
          body: body
        })
          .withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            resolve(response.status);
          }).catch(error => {
            console.log(error);
          });
      } else {
        resolve(null);
      }
    });
  }
}